import React from 'react';
import { ContentSection } from '@thd-olt-component-react/content-section';
import { EmtDescription } from '@thd-olt-component-react/emt-generated-content';
import { LinkTag, ScriptTag } from '@thd-nucleus/thd-helmet';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { NoIndexMetadata, NoIndexNoFollowMetadata } from '@thd-olt-component-react/metadata';
import { getComponentData } from '@thd-olt-functional/utils';
import { ContentAccordionSection } from '../specialtyContainers/ContentAccordionSection';
import { SKIP_AKAMAI_RESIZE, SSR_ALLOWED_COMPONENTS } from '../../constants';

// TODO: [AME-2113] This is a temporary fix-forward but will need to remove this in near future when stable.
export const seoTagPreloader = ({ data }) => {
  const seo = data?.layouts?.seo;
  const tags = [];

  if (!seo) return tags;

  const linkData = seo.linkData;
  const scriptData = seo.scriptData;
  /* Waiting for CIA changes in order to move this logic to the Metadata component. Implementation pending */
  if (linkData) {
    const linkAttrs = linkData.split(' ');
    const hrefAttrs = linkAttrs.find((attr) => attr.startsWith('href'));
    const href = hrefAttrs ? hrefAttrs.split('=')[1] : '';

    const linkTag = new LinkTag({
      id: 'alternate', 'data-th': 'server', rel: 'alternate', media: 'only screen and (max-width: 640px)', href: href.substring(1, href.length - 1)
    });
    tags.push(linkTag);
  }

  if (scriptData) {
    const splitScript = scriptData.split('>')[1].split('<')[0];
    const scriptTag = new ScriptTag({ 'data-th': 'server', type: 'application/ld+json', id: 'breadcrumbStructuredData', content: splitScript });
    tags.push(scriptTag);
  }

  return tags;
};

// Appends Image Manager query params to request a more appropriate sized hero image size.
const akamaiImageResize = (imageUrlToTransform) => {
  // Specific to Hero image size.
  const queryParams = 'Resize=(920,575)';

  if (!imageUrlToTransform) {
    return imageUrlToTransform;
  }
  const isAkamaiHost = imageUrlToTransform.includes('thdstatic.com');
  if (!isAkamaiHost) {
    return imageUrlToTransform;
  }
  if (!imageUrlToTransform.includes('?')) {
    return `${imageUrlToTransform}?im=${queryParams}`;
  }
  if (imageUrlToTransform.includes('im=')) {
    const urlBeforeImParams = imageUrlToTransform.substring(0, imageUrlToTransform.indexOf('im='));
    const imAndRemainderParams = imageUrlToTransform.substring(imageUrlToTransform.indexOf('im='));
    const hasAdditionalParams = imAndRemainderParams.includes('&');
    if (hasAdditionalParams) {
      const remainderOfParams = imAndRemainderParams.substring(imAndRemainderParams.indexOf('&'));
      const imParams = imAndRemainderParams.substring(0, imAndRemainderParams.indexOf('&'));
      return `${urlBeforeImParams}${imParams};${queryParams}${remainderOfParams}`;
    }
    return `${imageUrlToTransform};${queryParams}`;
  }
  return `${imageUrlToTransform}&im=${queryParams}`;
};

const getImageUrl = (imageData) => {
  return imageData?.damContentSelector?.assetData?.[0]?.selectedImageUrl
    || imageData?.damDownloadedContent?.url;
};

const prepareImageUrl = (url, typename) => {
  if (!url) return null;
  const isGif = url.split('?')[0].endsWith('.gif');
  if (isGif || SKIP_AKAMAI_RESIZE.includes(typename)) return url;
  return akamaiImageResize(url);
};

/**
 * Resolves ComponentSelector wrappers (and nested Slot wrappers) to the
 * underlying content component. Safe to call on any component — non-wrapper
 * components are returned as-is.
 */
const resolveComponent = (component) => {
  if (!component) return null;
  if (component.__typename === 'ComponentSelector') {
    const { defaultComponent } = component;
    if (defaultComponent?.__typename === 'Slot') {
      return defaultComponent.content;
    }
    return defaultComponent;
  }
  return component;
};

// To help lower LCP, preload the first image from heroCarousel or flexibleCluster1.
// Supports Hero/HeroFlattenImage (previewImage/mobilePreviewImage) and Newspaper/MedioInline
// (desktopImage/mobileImage) components. When desktop and mobile images differ, both are preloaded
// with media queries at 768px so the browser only downloads the one relevant to the device.
// Tablet image is intentionally skipped — tablet traffic is ~3% and not worth the added complexity.
export const imagePreloader = ({ data: contentData }) => {
  const heroCarousel = contentData?.layouts?.content?.heroCarousel;
  const flexibleCluster1 = contentData?.layouts?.content?.flexibleCluster1?.[0] || null;
  if (!heroCarousel && !flexibleCluster1) {
    return [];
  }

  const firstComponent = heroCarousel?.components?.[0] || flexibleCluster1?.components?.[0];
  if (!firstComponent) return [];

  const componentWithImage = resolveComponent(firstComponent);
  if (!componentWithImage) return [];

  const typename = componentWithImage?.__typename;

  const desktopImageUrl = getImageUrl(componentWithImage?.previewImage)
    || getImageUrl(componentWithImage?.desktopImage)
    || componentWithImage?.image?.url;

  const mobileImageUrl = getImageUrl(componentWithImage?.mobilePreviewImage)
    || getImageUrl(componentWithImage?.mobileImage);

  if (!desktopImageUrl) return [];

  const desktopUrl = prepareImageUrl(desktopImageUrl, typename);
  const mobileUrl = prepareImageUrl(mobileImageUrl, typename);

  const tagsList = [];

  if (mobileUrl && mobileUrl !== desktopUrl) {
    tagsList.push(
      new LinkTag({
        rel: 'preload',
        fetchPriority: 'high',
        href: desktopUrl,
        id: 'preloadFirstImg',
        as: 'image',
        media: '(min-width: 768px)'
      }),
      new LinkTag({
        rel: 'preload',
        fetchPriority: 'high',
        href: mobileUrl,
        id: 'preloadFirstImgMobile',
        as: 'image',
        media: '(max-width: 767px)'
      })
    );
  } else {
    tagsList.push(
      new LinkTag({
        rel: 'preload',
        fetchPriority: 'high',
        href: desktopUrl,
        id: 'preloadFirstImg',
        as: 'image'
      })
    );
  }

  return tagsList;
};

export const parseContent = ({ componentMapper, data, customProps = {}, layout }) => {
  if (!data?.layouts?.content) {
    return {};
  }

  const { content } = data.layouts;

  const Content = {};
  const promoVisNavTitles = {};

  const determineComponentType = (component) => {
    const typenameFromComponentProps = component.__typename;

    // If any of the following fields of the hero component coming from Contentful API are not empty, then it is a
    // BrandHero. ComponentMapper already maps the Hero component to the Hero harmony component, so if it is a
    // BrandHero, we need to change the typename to BrandHero manually.
    const {
      altTextForLogoBadge,
      buttonColor,
      logoOrBadge
    } = component;
    const isBrandHero = !!altTextForLogoBadge || !!buttonColor || !!logoOrBadge;

    return (typenameFromComponentProps === 'Hero' && isBrandHero) ? 'BrandHero' : typenameFromComponentProps;
  };

  function renderComponent(component, position, componentProps = {}) {
    const typename = determineComponentType(component);

    const { componentName } = componentProps;

    // The below logic was added because there are two title values: one on the Section level, and the other on the Component level.
    // As the renderComponent is recursive, we ensure that we save the right Section level title value into promoVisNavTitles and later reassign the value to the Component level title.
    if (componentName === 'promoVisualNavigation1'
      || componentName === 'promoVisualNavigation2'
      || componentName === 'promoVisualNavigation3') {
      const id = component.components[0].id;
      promoVisNavTitles[id] = componentProps.title;
    }

    const livePreviewData = getComponentData(data, component.id);
    const allComponentProps = { ...componentProps, ...(componentName === 'heroCarousel' && { title: null }), livePreviewData };

    switch (typename) {
    case 'BrandHero':
      allComponentProps.withSideNav = true;
      break;
    case 'Hero':
      allComponentProps.hasSidenav = true;
      break;
    case 'SpecialBuyOfTheDay':
      allComponentProps.isUppercaseHeader = true;
      break;
    case 'Sweepstakes':
      allComponentProps.useRestAPI = true;
      break;
    case 'VisualNavigation':
      allComponentProps.isInSection = true;
      allComponentProps.experienceHasSideNav = true;
      break;
    case 'PromoVisualNavigation':
      allComponentProps.experienceHasSideNav = true;
      break;
    case 'EndCap':
      allComponentProps.theme = component?.theme;
      break;
    default:
      // leave as is
      break;
    }

    let props = {
      componentId: component.id,
      // componentClass is not set to typename because if typename is set to 'BrandHero' (see determineComponentType
      // function), componentClass needs to be set to 'Hero' in order for the BrandHero component to render without
      // issue.
      componentClass: component.__typename,
      componentPosition: position,
      key: position,
      ...((typename === 'SpecialBuyOfTheDay' || typename === 'EndCap') && { entryId: component.id }),
      ...(typename === 'PromoVisualNavigation' && { title: promoVisNavTitles[component.id] || componentProps.dynamicPromoVisNavTitle }),
      ...(typename === 'Description_New' && { title: component.title, description: component.descriptionText, useDefaultFontStylesForHeadings: false }),
      ...allComponentProps,
      ...customProps[typename] && customProps[typename](component)
    };

    const newDynamicComponent = React.createElement(
      componentMapper.getComponent(typename),
      props,
      component.components?.map((comp, index) => renderComponent(comp, index + 1))
    );

    const impressableComponents = ['HeroCarousel', 'Section', 'ProductShelf', 'HeroFlattenImage', 'Sweepstakes'];

    if (impressableComponents.includes(typename)) {
      const isProductShelf = typename === 'ProductShelf';
      const isHeroCarousel = componentName === 'heroCarousel';
      const isHeroFlattenImage = typename === 'HeroFlattenImage';
      const isSweepstakes = typename === 'Sweepstakes';

      const impressionData = {
        id: component.id,
        component: isProductShelf ? 'ProductShelf' : 'Section',
        name: 'Section',
        type: isProductShelf ? 'product' : 'content',
      };
      if (isProductShelf) {
        impressionData.name = 'ProductShelf';
      }
      if (isHeroCarousel) {
        impressionData.name = 'HeroCarousel';
      }
      if (isHeroFlattenImage) {
        impressionData.name = 'HeroFlattenImage';
      }
      if (isSweepstakes) {
        impressionData.component = 'SweepstakesEntryCard';
        impressionData.name = 'SweepstakesEntryCard';
      }

      return (
        <ImpressionProvider
          key={position || 1}
          data={impressionData}
        >
          {newDynamicComponent}
        </ImpressionProvider>
      );
    }

    return newDynamicComponent;
  }

  function addDynamicSection(dynamicSection, keyName) {
    Content[keyName] = [];

    dynamicSection.forEach((subSection) => {
      let {
        isCarousel, title, id, theme, itemTheme, sectionCTAButton
      } = subSection;
      const componentName = subSection.components[0].__typename;
      const dynamicPromoVisNavTitle = componentName === 'PromoVisualNavigation' ? title : '';

      const subSectionProps = isCarousel ? {
        spaceBetweenXs: 16,
        spaceBetweenSm: 24,
        spaceBetweenMd: 24,
        spaceBetweenLg: 32,
        spaceBetweenXl: 32,
        slidesLg: 3,
        slidesXl: 3,
        slidesMd: 3,
        slidesSm: 2,
        slidesXs: 2,
      } : {};

      // Determine section type to use
      let sectionType;
      let disableGrid = false;
      switch (componentName) {
      case 'ContentAccordion':
        sectionType = ContentAccordionSection;
        break;
      default:
        sectionType = ContentSection;
        // Disable grid layout for Sweepstakes to allow full-width display
        disableGrid = componentName === 'Sweepstakes';
        break;
      }

      const childSection = React.createElement(
        sectionType,
        {
          isCarousel, itemTheme, title, theme, sectionCTAButton, disableGrid, ...subSectionProps
        },
        subSection.components?.map((comp, index) => renderComponent(comp, index + 1, { dynamicPromoVisNavTitle }))
      );
      Content[keyName].push(() => React.createElement(
        ImpressionProvider,
        {
          data: {
            id,
            component: 'Section',
            type: 'content',
            name: 'Section'
          }
        },
        childSection
      ));
    });
  }

  function addDescriptionNew(richText, keyName) {
    Content[keyName] = [];

    richText.forEach((descriptionNew) => {
      let { title, description, descriptionText } = descriptionNew;

      Content[keyName].push(() => React.createElement(
        EmtDescription,
        { title, description: descriptionText, descriptionRichText: description, useDefaultFontStylesForHeadings: false }
      ));
    });
  }

  function addComponent(componentName, component) {
    let {
      isCarousel, title, theme, itemTheme, ...otherProps
    } = component;
    let isSection = component.__typename === 'Section' || component.__typename === 'HeroCarousel';
    const sectionProps = isSection ? {
      isCarousel, title, componentName, theme, itemTheme, ...otherProps
    } : {};

    Content[componentName] = (props) => {
      return renderComponent(component, 1, { ...sectionProps, ...props });
    };
  }

  componentMapper.getMainContentContentfulKeys(layout).map((key) => {
    const isDynamicSection = key.includes('dynamicSections') || key.includes('flexibleCluster');
    const isRichText = key.includes('richText');
    const contentSection = content[key];
    const typename = contentSection?.__typename;
    if (Array.isArray(contentSection)) {
      if (isDynamicSection) {
        addDynamicSection(contentSection, key);
      }
      if (isRichText) {
        addDescriptionNew(contentSection, key);
      }
    } else if (componentMapper.getComponent(typename)) {
      addComponent(key, contentSection);
    } else if (!contentSection) {
      // Even though section content does not exist it still needs to be added to the Content object with a default
      // value set to an empty React Fragment. If we do not do this, the calling code that tries to reference this
      // component will cause an error and prevent the page from loading.

      Content[key] = isDynamicSection ? [] : () => React.Fragment;
    }

    return null;
  });

  return {
    Content
  };
};

export const parseSponsoredContent = ({ componentMapper, data, customProps = {}, layout }) => {
  if (!data?.layouts?.content) {
    return {};
  }

  const { content } = data.layouts;

  const Sponsored = {};

  function renderComponent(component, position, componentProps = {}) {
    const typename = component.__typename;

    if (!componentMapper.getComponent(typename)) {
      return null;
    }

    const allComponentProps = { ...componentProps };

    let props = {
      componentId: component.id,
      componentClass: typename,
      componentPosition: position,
      key: position,
      ...allComponentProps,
      ...customProps[typename] && customProps[typename](component)
    };

    const newDynamicComponent = React.createElement(
      componentMapper.getComponent(typename),
      props,
      component.components?.map((comp, index) => renderComponent(comp, index + 1))
    );
    return newDynamicComponent;
  }

  function addComponent(componentName, component) {
    Sponsored[componentName] = (props) => renderComponent(component, 1, { ...props });
  }

  componentMapper.getSponsoredContentContentfulKeys(layout).map((key) => {
    const contentSection = content[key];
    const typename = contentSection?.__typename;

    if (componentMapper.getComponent(typename)) {
      addComponent(key, contentSection);
    } else if (!contentSection) {
      Sponsored[key] = () => React.Fragment;
    }
    return null;
  });

  return {
    Sponsored: {
      ...Sponsored
    }
  };
};

export const renderRobotTag = (data) => {
  if (data.no_follow && data.no_index) {
    return <NoIndexNoFollowMetadata />;
  }

  if (data.no_index) {
    return <NoIndexMetadata />;
  }

  return null;
};

/**
 * Checks if a section's first component type is safe to SSR.
 * Resolves ComponentSelector wrappers to the actual content type.
 */
export const canSSRSection = (section) => {
  const component = section?.components?.[0];
  const resolved = resolveComponent(component);
  if (!resolved) return false;

  return SSR_ALLOWED_COMPONENTS.includes(resolved.__typename);
};

export const getUrlParameter = (paramName) => {
  const urlParams = new URLSearchParams(window?.location?.search);
  const namedParam = urlParams?.get(paramName);
  return namedParam ? `${paramName}=${encodeURIComponent(namedParam)}` : '';
};
